import React, { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Loader2,
  Info,
  TrendingUp,
  Package,
  Target,
  Zap,
  Database
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

interface AttributeEvaluation {
  id: string;
  attribute_name: string;
  product_count: number;
  evaluation_type: string;
  recommendation: 'research' | 'disregard';
  reasoning: string;
  value_examples: {
    customerValue: 'high' | 'medium' | 'low';
    researchPriority: number;
  };
  user_decision: 'approved' | 'rejected' | null;
  created_at: string;
}

interface DataSource {
  id: string;
  file_name: string;
  source_type: string;
  records_count: number;
}

interface EvaluationStats {
  total: number;
  toResearch: number;
  toDisregard: number;
  approved: number;
  rejected: number;
  pending: number;
}

const evaluationTypeIcons: Record<string, React.ReactNode> = {
  technical_spec: <Zap className="h-4 w-4" />,
  customer_benefit: <TrendingUp className="h-4 w-4" />,
  use_case: <Target className="h-4 w-4" />,
  compatibility: <Database className="h-4 w-4" />,
  metadata: <Info className="h-4 w-4" />,
  packaging: <Package className="h-4 w-4" />,
};

const evaluationTypeColors: Record<string, string> = {
  technical_spec: 'bg-blue-100 text-blue-800',
  customer_benefit: 'bg-green-100 text-green-800',
  use_case: 'bg-purple-100 text-purple-800',
  compatibility: 'bg-orange-100 text-orange-800',
  metadata: 'bg-gray-100 text-gray-800',
  packaging: 'bg-yellow-100 text-yellow-800',
};

export default function AttributeApproval() {
  const { selectedSite } = useSite();
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [selectedDataSource, setSelectedDataSource] = useState<string>('');
  const [evaluations, setEvaluations] = useState<AttributeEvaluation[]>([]);
  const [stats, setStats] = useState<EvaluationStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [selectedAttributes, setSelectedAttributes] = useState<Set<string>>(new Set());
  const [processingDecisions, setProcessingDecisions] = useState(false);
  const [creatingTopics, setCreatingTopics] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (selectedSite) {
      loadDataSources();
    }
  }, [selectedSite]);

  useEffect(() => {
    if (selectedDataSource) {
      loadEvaluations();
    }
  }, [selectedDataSource]);

  const loadDataSources = async () => {
    if (!selectedSite) return;

    try {
      const response = await authFetch(`${API_URL}/data-sources/list/${selectedSite.id}`);
      const result = await response.json();

      if (result.success) {
        const productFeeds = result.dataSources.filter((ds: DataSource) => ds.source_type === 'product_feed');
        setDataSources(productFeeds);
      }
    } catch (error) {
      console.error('Error loading data sources:', error);
    }
  };

  const loadEvaluations = async () => {
    if (!selectedDataSource) return;

    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/attribute-evaluation/data-sources/${selectedDataSource}/evaluations`);
      const result = await response.json();

      if (result.success) {
        setEvaluations([
          ...result.data.toResearch,
          ...result.data.toDisregard,
          ...result.data.approved,
          ...result.data.rejected
        ]);
        setStats(result.data.stats);
      }
    } catch (error) {
      console.error('Error loading evaluations:', error);
    } finally {
      setLoading(false);
    }
  };

  const runEvaluation = async () => {
    if (!selectedDataSource || !selectedSite) return;

    setEvaluating(true);
    setMessage(null);

    try {
      const response = await authFetch(`${API_URL}/attribute-evaluation/data-sources/${selectedDataSource}/evaluate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sectorId: selectedSite.sector_id,
          minProductThreshold: 5,
          batchSize: 10
        })
      });

      const result = await response.json();

      if (result.success) {
        setMessage({
          type: 'success',
          text: `Evaluated ${result.data.totalAttributes} attributes: ${result.data.toResearch.length} to research, ${result.data.toDisregard.length} to disregard`
        });
        await loadEvaluations();
      } else {
        setMessage({ type: 'error', text: result.error || 'Evaluation failed' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Evaluation failed' });
    } finally {
      setEvaluating(false);
    }
  };

  const updateDecision = async (evaluationIds: string[], decision: 'approved' | 'rejected') => {
    setProcessingDecisions(true);
    setMessage(null);

    try {
      const response = await authFetch(`${API_URL}/attribute-evaluation/evaluations/bulk-decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ evaluationIds, decision })
      });

      const result = await response.json();

      if (result.success) {
        setMessage({
          type: 'success',
          text: `Updated ${result.data.success.length} attributes to ${decision}`
        });
        setSelectedAttributes(new Set());
        await loadEvaluations();
      } else {
        setMessage({ type: 'error', text: result.error || 'Update failed' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Update failed' });
    } finally {
      setProcessingDecisions(false);
    }
  };

  const createTopics = async () => {
    if (!selectedDataSource || !selectedSite) return;

    setCreatingTopics(true);
    setMessage(null);

    try {
      const response = await authFetch(`${API_URL}/attribute-evaluation/data-sources/${selectedDataSource}/create-topics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sectorId: selectedSite.sector_id })
      });

      const result = await response.json();

      if (result.success) {
        setMessage({
          type: 'success',
          text: `Created ${result.data.topicsCreated} topics from approved attributes`
        });
        await loadEvaluations();
      } else {
        setMessage({ type: 'error', text: result.error || 'Topic creation failed' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Topic creation failed' });
    } finally {
      setCreatingTopics(false);
    }
  };

  const toggleAttributeSelection = (id: string) => {
    const newSelection = new Set(selectedAttributes);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedAttributes(newSelection);
  };

  const selectAll = (list: AttributeEvaluation[]) => {
    const newSelection = new Set(selectedAttributes);
    list.forEach(evaluation => newSelection.add(evaluation.id));
    setSelectedAttributes(newSelection);
  };

  const deselectAll = () => {
    setSelectedAttributes(new Set());
  };

  const pendingEvaluations = evaluations.filter(e => !e.user_decision);
  const toResearchList = pendingEvaluations.filter(e => e.recommendation === 'research');
  const toDisregardList = pendingEvaluations.filter(e => e.recommendation === 'disregard');
  const approvedList = evaluations.filter(e => e.user_decision === 'approved');

  const renderAttributeCard = (evaluation: AttributeEvaluation, canMove: boolean = true) => {
    const isSelected = selectedAttributes.has(evaluation.id);
    const typeColor = evaluationTypeColors[evaluation.evaluation_type] || 'bg-gray-100 text-gray-800';
    const typeIcon = evaluationTypeIcons[evaluation.evaluation_type];

    return (
      <Card
        key={evaluation.id}
        className={`mb-3 transition-all ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              {canMove && (
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleAttributeSelection(evaluation.id)}
                  className="mt-1"
                />
              )}
              <div className="flex-1">
                <CardTitle className="text-base font-semibold mb-2">
                  {evaluation.attribute_name}
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={typeColor}>
                    <span className="flex items-center gap-1">
                      {typeIcon}
                      {evaluation.evaluation_type.replace('_', ' ')}
                    </span>
                  </Badge>
                  <Badge variant="outline">
                    {evaluation.product_count} products
                  </Badge>
                  {evaluation.value_examples && (
                    <>
                      <Badge
                        variant="outline"
                        className={
                          evaluation.value_examples.customerValue === 'high'
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : evaluation.value_examples.customerValue === 'medium'
                            ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                            : 'bg-gray-50 text-gray-700 border-gray-200'
                        }
                      >
                        {evaluation.value_examples.customerValue} value
                      </Badge>
                      <Badge variant="outline">
                        Priority: {evaluation.value_examples.researchPriority}/10
                      </Badge>
                    </>
                  )}
                </div>
              </div>
            </div>
            {evaluation.user_decision && (
              <Badge
                variant="outline"
                className={evaluation.user_decision === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
              >
                {evaluation.user_decision}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-700 leading-relaxed">
            {evaluation.reasoning}
          </p>
        </CardContent>
      </Card>
    );
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Please select a site from the header to manage attribute evaluations.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Attribute Evaluation & Approval</h1>
        <p className="text-gray-600">
          AI-powered analysis of product attributes to determine which should become research topics
        </p>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Data Source Selection</CardTitle>
          <CardDescription>
            Select a product feed to evaluate its attributes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Product Feed</label>
              <Select value={selectedDataSource} onValueChange={setSelectedDataSource}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a product feed" />
                </SelectTrigger>
                <SelectContent>
                  {dataSources.map(ds => (
                    <SelectItem key={ds.id} value={ds.id}>
                      {ds.file_name} ({ds.records_count} products)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={runEvaluation}
              disabled={!selectedDataSource || evaluating}
              className="flex items-center gap-2"
            >
              {evaluating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Evaluating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Run AI Evaluation
                </>
              )}
            </Button>
          </div>

          {message && (
            <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
              <AlertDescription>{message.text}</AlertDescription>
            </Alert>
          )}

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
              <div className="text-center">
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-sm text-gray-600">Total Attributes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{stats.toResearch}</div>
                <div className="text-sm text-gray-600">To Research</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">{stats.toDisregard}</div>
                <div className="text-sm text-gray-600">To Disregard</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.approved}</div>
                <div className="text-sm text-gray-600">Approved</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Bar */}
      {selectedAttributes.size > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="font-medium">{selectedAttributes.size} selected</span>
                <Button variant="ghost" size="sm" onClick={deselectAll}>
                  Deselect All
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => updateDecision(Array.from(selectedAttributes), 'approved')}
                  disabled={processingDecisions}
                  variant="default"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Approve Selected
                </Button>
                <Button
                  onClick={() => updateDecision(Array.from(selectedAttributes), 'rejected')}
                  disabled={processingDecisions}
                  variant="destructive"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Reject Selected
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : pendingEvaluations.length === 0 && evaluations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Sparkles className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium mb-2">No Evaluations Yet</h3>
            <p className="text-gray-600">
              Select a product feed and run AI evaluation to get started
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Two-Column Layout */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* To Research Column */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      To Research ({toResearchList.length})
                    </CardTitle>
                    <CardDescription>
                      AI recommends researching these attributes
                    </CardDescription>
                  </div>
                  {toResearchList.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => selectAll(toResearchList)}
                    >
                      Select All
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="max-h-[600px] overflow-y-auto">
                {toResearchList.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No attributes to research</p>
                ) : (
                  toResearchList.map(evaluation => renderAttributeCard(evaluation))
                )}
              </CardContent>
            </Card>

            {/* To Disregard Column */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-gray-600" />
                      To Disregard ({toDisregardList.length})
                    </CardTitle>
                    <CardDescription>
                      AI recommends not researching these attributes
                    </CardDescription>
                  </div>
                  {toDisregardList.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => selectAll(toDisregardList)}
                    >
                      Select All
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="max-h-[600px] overflow-y-auto">
                {toDisregardList.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No attributes to disregard</p>
                ) : (
                  toDisregardList.map(evaluation => renderAttributeCard(evaluation))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Approved Attributes */}
          {approvedList.length > 0 && (
            <Card className="border-green-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="h-5 w-5" />
                      Approved Attributes ({approvedList.length})
                    </CardTitle>
                    <CardDescription>
                      Ready to create research topics
                    </CardDescription>
                  </div>
                  <Button
                    onClick={createTopics}
                    disabled={creatingTopics}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                  >
                    {creatingTopics ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Creating Topics...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Create Topics from Approved
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="max-h-[400px] overflow-y-auto">
                <div className="grid md:grid-cols-2 gap-4">
                  {approvedList.map(evaluation => renderAttributeCard(evaluation, false))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
